<template>
  <div>
    <div class="funnel__registration" @click="actionReset">
      <h2 class="title_funnel">
        <template v-if="isEditMode">{{ $t('funnel.edit.title') }}</template>
        <template v-else>{{ $t('funnel.registration.title') }}</template>
      </h2>
      <div class="box__registration--wrap">
        <div class="wrap__registration">
          <div class="domain_info">
            <label>{{ $t('common.domain') }}</label>
            <div class="wrap_domain ellipsis">{{ realDomain }}</div>
          </div>
          <div class="domain_info">
            <label for="fn_name" class="funnel_name">
              {{ $t('funnel.registration.form.funnelName.label') }}
            </label>
            <div class="wrap__inp" :class="{inp_error: isValidatorEnabled && funnelNameError}">
              <input type="text" id="fn_name" class="inp_funnel"
                :placeholder="$t('common.placeHolder.enterFunnelName')"
                v-model.trim="funnelName">
              <span class="icon icon--close14"
                v-if="funnelName.length"
                @click="funnelName = ''"></span>
              <strong v-if="isValidatorEnabled && funnelNameError" class="error">{{ funnelNameError }}</strong>
            </div>
          </div>
        </div>

        <div class="wrap--steps">
          <draggable v-if="funnelItems"
            tag="ul" class="list_step"
            :list="funnelItems"
            :disabled="isEditMode"
            :preventOnFilter="false"
            filter="input, button"
            @end="onDragEnd">
            <li v-for="(item, index) in funnelItems" :key="index" class="clearfix step_item">
              <div class="inner_step">
                <!-- todo: i18n (Step) -->
                <span class="step_desc" :class="{ 'edit-mode': isEditMode }">Step {{ index + 1}}</span>
                <input type="text" :class="{inp_error: isValidatorEnabled && !item.stepName.length}"
                  class="inp_step_name"
                  :placeholder="$t('common.placeHolder.enterStepName')" v-model.trim="item.stepName">
                <span class="icon icon--close14"
                  v-if="item.stepName.length"
                  @click="item.stepName = ''"></span>
                <strong v-if="isValidatorEnabled && !item.stepName.length" class="error">
                  {{ $t('common.validationErrorText.funnel.stepName.required') }}
                </strong>
              </div>
              <div class="wrap_selectbox" @mouseover="item.isShowCopy = true" @mouseleave="item.isShowCopy = false">
                <ui-selectbox
                  class="aid_selectbox"
                  v-model="item.aid"
                  :items="getAvailableApplies(item)"
                  :class="{error: isValidatorEnabled && !item.aid.length}"
                  :disabled="isEditMode"/>
                <strong v-if="isValidatorEnabled && !item.aid.length" class="error">
                  {{ $t('common.validationErrorText.funnel.pageUrl.selected') }}
                </strong>
                <transition name="fade">
                  <div class="copy" v-if="item.isShowCopy && isEditMode">
                    <button type="button" class="icon icon--copy" @click.stop="copyCode(item, $event)"></button>
                  </div>
                </transition>
                <button type="button" class="icon icon--delete" v-if="funnelItems.length > 2"
                  @click="onFunnelItemDeleteClick(index)">
                  {{ $t('common.delete') }}
                </button>
                <div class="content__toast" v-if="item.showCopyToastLayer && isEditMode">
                  <span>{{ $t('common.trackingCode.copied') }}</span>
                  <i class="icon icon--check-green-size16"></i>
                </div>
              </div>
            </li>
          </draggable>
          <div class="wrap_add" v-if="!isEditMode">
            <button type="button"
                    class="btn_add"
                    :class="{disabled: checkFunnelStepLength}"
                    @click="onAddStepClick">
              <i class="icon icon--plus-black"></i>
              <span class="text">{{ $t('funnel.registration.form.funnelName.addStep') }}</span>
            </button>
          </div>
        </div>
      </div>
      <div class="button--wrap">
        <ui-button @click="$router.push({ name: 'funnel.list', params: { aid: $route.params.aid } })">
          {{ $t('common.cancel') }}
        </ui-button>
        <ui-button primary @click="onSubmitClick" class="btn__register">
          {{ isEditMode ? $t('common.edit') : $t('common.register') }}
        </ui-button>
      </div>
      <div class="wrap_caution">
        <strong class="txt_desc">
          {{ $t('funnel.registration.caution.title') }}
        </strong>
        <ul class="list_caution">
          <li v-html="$t('funnel.registration.caution.desc1')"></li>
          <li v-html="$t('funnel.registration.caution.desc2')"></li>
        </ul>
      </div>
    </div>
  </div>
</template>

<script>
import { mapState } from 'vuex'
import draggable from 'vuedraggable'
import { getPageListBySid } from '@/api/apply'
import { getFunnel, insertFunnel } from '@/api/funnel'
import UiButton from '@/components/ui/UiButton'
import UiSelectbox from '@/components/ui/UiSelectbox'

function getEmptyFunnelItem () {
  return {
    stepName: '',
    aid: '',
    url: ''
  }
}

export default {
  name: 'FunnelAddOrEdit',
  components: {
    draggable,
    UiSelectbox,
    UiButton
  },
  data () {
    return {
      isValidatorEnabled: false,
      maxStep: 20,
      funnelName: '',
      funnelItems: [],
      applies: []
    }
  },
  computed: {
    ...mapState([
      'applyData'
    ]),
    aid () {
      return this.applyData._id
    },
    fid () {
      return this.$route.params.fid
    },
    isEditMode () {
      return !!this.fid
    },
    realDomain () {
      return this.applyData.real_domain
    },
    usedAidSet () {
      const aidSet = {}

      for (const item of this.funnelItems) {
        if (item.aid) {
          aidSet[item.aid] = true
        }
      }
      return aidSet
    },
    funnelNameError () {
      if (!this.funnelName.trim().length) {
        return this.$t('common.validationErrorText.funnel.funnelName.required')
      }
      return null
    },
    checkFunnelStepLength () {
      return this.funnelItems.length >= this.maxStep
    }
  },
  created () {
    getPageListBySid(this.applyData.sid).then(items => {
      for (const item of items) {
        const applies = item.applies.filter(item => item.state === 'run').map(item => {
          return {
            aid: item.id,
            pageName: item.pageName,
            matchType: item.matchType,
            url: item.url
          }
        })
        this.applies = this.applies.concat(applies)
      }

      if (this.fid) {
        return getFunnel(this.fid)
      } else {
        return Promise.resolve(false)
      }
    }).then(funnelData => {
      if (funnelData) {
        this.funnelName = funnelData.name
        this.funnelItems = funnelData.funnelList.map(item => {
          return {
            aid: item.aid,
            stepName: item.name,
            url: item.url,
            isShowCopy: false,
            showCopyToastLayer: false
          }
        })
      } else {
        this.onAddStepClick()
        this.onAddStepClick()
      }
    })
  },
  methods: {
    actionReset () {
      this.funnelItems.forEach(item => {
        item.showCopyToastLayer = false
      })
    },
    copyCode (item, event) {
      this.funnelItems.forEach(item => {
        item.showCopyToastLayer = false
      })
      const parentElem = event.target.parentNode.parentNode
      const targetElem = parentElem.querySelector('span.ellipsis')
      if (targetElem) {
        let range = document.createRange()
        range.setStartBefore(targetElem)
        range.setEndAfter(targetElem)
        let sel = window.getSelection()
        sel.removeAllRanges()
        sel.addRange(range)
        document.execCommand('copy')
        item.showCopyToastLayer = true
        setTimeout(() => {
          item.showCopyToastLayer = false
        }, 5000)
      }
    },
    onDragEnd () {
    },
    onAddStepClick () {
      if (this.funnelItems.length < this.maxStep) {
        this.funnelItems.push(getEmptyFunnelItem())
      }
    },
    onFunnelItemDeleteClick (index) {
      this.funnelItems.splice(index, 1)
    },
    onSubmitClick () {
      this.isValidatorEnabled = true
      if (this.funnelNameError) {
        return
      }

      for (const item of this.funnelItems) {
        if (!item.stepName.trim().length || !item.aid) {
          return
        }
      }

      const model = {
        name: this.funnelName,
        domain: this.realDomain,
        // sid: this.applyData.sid,
        funnelName: this.funnelItems.map(item => item.stepName),
        funnelAid: this.funnelItems.map(item => item.aid)
      }

      if (this.isEditMode) {
        model.fid = this.fid
      }

      insertFunnel(model).then(data => {
        this.$router.push({ name: 'funnel.list', params: { aid: this.aid } })
      }).catch(err => {
        console.error(err)
      })
    },
    getAvailableApplies (currentItem) {
      if (!this.applies) {
        return []
      }

      if (this.isEditMode) {
        return [
          {
            value: currentItem.aid,
            text: currentItem.url
          }
        ]
      }

      const currentAid = currentItem.aid
      const items = this.applies.filter(item => {
        return item.aid === currentAid || !this.usedAidSet[item.aid]
      }).map(item => {
        const matchTypeStr = this.$options.filters.matchType(item.matchType)
        return {
          value: item.aid,
          text: `[${matchTypeStr}] ${item.pageName}`,
          pageName: item.pageName
        }
      }).sort((a, b) => {
        return a.pageName.localeCompare(b.pageName)
      })
      items.unshift({
        value: '',
        text: this.$t('funnel.registration.form.selectPageUrl')
      })
      return items
    }
  }
}
</script>

<style lang="scss">
.fade-leave-active,
.fade-enter-active {
  transition: opacity 0.2s ease;
  opacity: 1;
}

.fade-enter,
.fade-leave-to {
  opacity: 0;
}

.funnel__registration {
  height: 100%;
  overflow-y: auto;

  .title_funnel{
    padding-top: 40px;
    width:770px;
    margin:0 auto;
    font-size:22px;
    color:#444;
  }
  .box__registration--wrap{
    width:770px;
    margin:0 auto;
    margin-top:25px;
    border-radius: 14px;
    box-shadow: 0 23px 15px -12px rgba(0, 0, 0, 0.08), 0 0 0 1px rgba(0, 0, 0, 0.35);
    background-color: #fff;
  }
  .wrap__registration{
    padding:30px 30px;
    .domain_info{
      position: relative;
      label{
        padding-left:6px;
        font-weight: 500;
        font-size: 13px;
        color: #767676;
      }
    }
    .domain_info + .domain_info{
      margin-top:40px;
    }
    .wrap_domain{
      width:100%;
      height:38px;
      margin-top:5px;
      line-height:38px;
      text-indent:12px;
      border-radius: 6px;
      border: solid 1px #d7d7d7;
      background-color: #e6e6e6;
      color:#999;
      font-size: 14px;

    }
    .funnel_name{
      display:block;
      padding-left:6px;
    }
    .wrap__inp{
      position: relative;
      height:38px;
      margin-top:5px;
      .inp_funnel{
        width:100%;
        height: 100%;
        text-indent: 12px;
        font-size: 14px;
        background-color: #fff;
        border-radius: 6px;
        border: solid 1px #bbbbbb;
        box-sizing: border-box;

        &:focus {
          border-color: #289eef;
        }
      }
      .icon--close14{
        position:absolute;
        top:50%;
        right:2px;
        margin-top:-17px;
        padding:10px 12px;
        cursor: pointer;
        background-position:center;
      }
    }
    .wrap__inp.inp_error{
        border:1px solid #e60724;
        padding-top:0;
        text-indent:0;
      }
    .error{
      top:42px;
      left:5px;
    }
  }
  .wrap--steps{
    padding:30px 30px;
    border-top:1px solid #ebebeb;
    .list_step{
        position:relative;
      &:before{
        content:'';
        position:absolute;
        top:0;
        left:47px;
        width:3px;
        height:100%;
        background-color:#aaa;
      }
      .step_item{
        position:relative;
        padding:26px 0;
        border-radius: 7px;
        box-shadow: 0 2px 4px 0 rgba(0, 0, 0, 0.12), 0 0 0 1px rgba(0, 0, 0, 0.12);
        background-color: #ffffff;
        .inner_step{
          position: relative;
          float:left;
          .step_desc{
            position: relative;
            padding-left:33px;
            font-size:14px;
            font-weight: 500;
            &:before{
              content:'';
              position:absolute;
              top:3px;
              left:10px;
              width:10px;
              height:14px;
              background:url(../../../../assets/img/funnel/funnel_grid_icon.svg) no-repeat;
              vertical-align: middle;
            }
            &.edit-mode {
              &:before {
                display: none;
              }
            }
          }
          .inp_step_name{
            width: 170px;
            height: 34px;
            margin-left:26px;
            border-radius: 6px;
            border: solid 1px #bbbbbb;
            background-color: #ffffff;
            text-indent:12px;
            font-size: 14px;

            &:focus {
              border-color: #289eef;
            }
          }
          .inp_step_name.inp_error{
            border:1px solid #e60724;
          }
          .icon--close14{
              position:absolute;
              top:50%;
              right:2px;
              margin-top:-17px;
              padding:10px 12px;
              cursor: pointer;
              background-position:center;
            }
          .error{
            top: 42px;
            left: 104px;
          }
        }
        .aid_selectbox{
          width:100%;
          margin-left:5px;

          &.error .item--wrap {
            border-color: #e60724
          }
        }
      }
      .step_item + .step_item{
        margin-top:20px;
      }
      .wrap_selectbox{
        position: relative;
        float:left;
        width:54%;

        & > .ui_select .item--wrap .selected-item .ellipsis {
          display: inline-block;
          width: calc(100% - 35px);
        }
      }
      .icon--delete{
        position:absolute;
        top:50%;
        right:-35px;
        margin-top:-8px;
        text-indent:-9999px;
      }
      .copy {
        width: 26px;
        height: 26px;
        border-radius: 13px;
        background-color: #ffffff;
        position:absolute;
        top:13px;
        right: 6px;
        margin-top:-8px;
        text-indent:-9999px;
        .icon--copy {
          position: absolute;
          top: 5px;
          left: 5px;
        }
      }

      .content__toast {
        display: inline-block;
        height: 32px;
        border-radius: 6px;
        box-shadow: 0 8px 15px 0 rgba(0, 0, 0, 0.16);
        background-color: rgba(0,0,0,0.85);
        position: absolute;
        top: 3px;
        left: 50%;
        transform: translateX(-50%);
        //width: 100%;
        //max-width: %;
        padding-left: 12px;
        padding-right: 45px;
        width: max-content;
        span {
          font-size: 13px;
          font-weight: 600;
          color: #57ab00;
          line-height: 32px;
        }
        i {
          position: absolute;
          top: 50%;
          right: 12px;
          transform: translateY(-50%);
        }
      }

    }
    .wrap_add{
      margin-top:20px;
      line-height:50px;
      border-radius: 7px;
      border:1px dashed #bbb;
      background-color: #f4f4f4;
      font-size:14px;
      text-align:center;
      .btn_add{
        width:100%;
        line-height:50px;

        &.disabled {
          opacity: 0.3;
        }

        .icon {
          margin-right: 5px;
        }
        .icon, .text {
          display: inline-block;
          vertical-align: middle;
        }
      }
    }
  }

  .button--wrap {
    margin-top: 30px;
    text-align: center;

    button {
      @include buttonTypeNormal;

      &.btn__cancel {
        border: solid 1px #bbbbbb;
        background-color: #ffffff;
        color: #444444;
      }
      &.btn__register {
        margin-left: 5px;
        background-color: #ec0047;
        color: #ffffff;

        &.disabled {
          pointer-events: none;
          opacity: 0.3;
        }
      }
    }
  }

  .wrap_caution{
    width:686px;
    margin: 50px auto 60px;
    font-weight: 500;

    .txt_desc{
      font-size:15px;
      color:#444;
      &:before{
        content:'';
        display:inline-block;
        width:16px;
        height:16px;
        margin-right:8px;
        background:url(../../../../assets/img/funnel/funnel_stop_icon.svg) no-repeat;
        vertical-align: top;
      }
    }
    .list_caution{
      margin-top:15px;
      padding:0 0 0 5px;
      li{
        font-size:12px;
        line-height:18px;
        &:before{
          content:'';
          display:inline-block;
          width:2px;
          height:2px;
          margin:9px 7px 0 -9px;
          vertical-align: top;
          background-color:#d8d8d8;
        }
      }
      li + li{
        padding-top:11px;
      }
    }
  }

  strong.error {
    position:absolute;
    top:40px;
    left:12px;
    color: #e60724;
    font-weight: 600;
  }
}
</style>
